import { toast } from "@/components/ui/use-toast";
import {
  useExportPayrollFiscalCycleMutation,
  useLazyExportPayrollRangeQuery,
} from "@/services/payroll-voucher";
import type { BlobDownload } from "@/utils/blob-response-handler";
import { downloadFromBlob } from "@/utils/download-blob";
import { useCallback } from "react";
import { PAYROLL_VOUCHER_TOASTS } from "../messages";
import { DateRange } from "../types";
import { parsePayrollVoucherError } from "./payroll-voucher-error";

export interface PayrollExportActions {
  exportRange: (range: DateRange) => void;
  exportFiscalCycle: (cycle: string) => void;
  isExporting: boolean;
}

const isDownload = (result: unknown): result is BlobDownload =>
  !!result && typeof result === "object" && "blob" in result;

export const usePayrollExport = (): PayrollExportActions => {
  const [triggerRange, rangeState] = useLazyExportPayrollRangeQuery();
  const [triggerCycle, cycleState] = useExportPayrollFiscalCycleMutation();

  const deliver = useCallback((result: unknown, label: string): void => {
    if (!isDownload(result)) {
      toast(PAYROLL_VOUCHER_TOASTS.DECISION_FAILED(label));
      return;
    }

    downloadFromBlob(result.blob, result.filename);
  }, []);

  const exportRange = useCallback(
    (range: DateRange): void => {
      void (async (): Promise<void> => {
        try {
          const result = await triggerRange(range).unwrap();
          deliver(result, PAYROLL_VOUCHER_TOASTS.EXPORT_EMPTY().description);
        } catch (error: unknown) {
          toast(
            PAYROLL_VOUCHER_TOASTS.DECISION_FAILED(
              parsePayrollVoucherError(error).description,
            ),
          );
        }
      })();
    },
    [triggerRange, deliver],
  );

  const exportFiscalCycle = useCallback(
    (cycle: string): void => {
      void (async (): Promise<void> => {
        try {
          const result = await triggerCycle({ cycle }).unwrap();
          deliver(result, PAYROLL_VOUCHER_TOASTS.EXPORT_EMPTY().description);
        } catch (error: unknown) {
          toast(
            PAYROLL_VOUCHER_TOASTS.DECISION_FAILED(
              parsePayrollVoucherError(error).description,
            ),
          );
        }
      })();
    },
    [triggerCycle, deliver],
  );

  return {
    exportRange,
    exportFiscalCycle,
    isExporting: rangeState.isLoading || cycleState.isLoading,
  };
};
